import { useEffect, useState } from "react";
import { Plus, Gift, Trash2, Edit3, Sparkles, Tag, Clock, Package, TrendingUp } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];
const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const emptyOffer = {
  title: "",
  description: "",
  discount_text: "",
  kind: "offer",
  gift_products: "",
  quantity_per_hour: 0,
  active_days: [0, 1, 2, 3, 4, 5, 6],
  active_start: "09:00",
  active_end: "21:00",
};

export default function OffersPage() {
  return (
    <DashboardShell active="offers">
      {() => <OffersBody />}
    </DashboardShell>
  );
}

function OffersBody() {
  const [offers, setOffers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyOffer);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([
        api.get("/merchants/me/offers"),
        api.get("/merchants/me/redemptions/summary"),
      ]);
      setOffers(o.data);
      setSummary(s.data);
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyOffer);
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      title: o.title,
      description: o.description,
      discount_text: o.discount_text || "",
      kind: o.kind,
      gift_products: o.gift_products || "",
      quantity_per_hour: o.quantity_per_hour || 0,
      active_days: o.active_days ?? [0, 1, 2, 3, 4, 5, 6],
      active_start: o.active_start || "09:00",
      active_end: o.active_end || "21:00",
    });
    setOpen(true);
  };

  const toggleDay = (d) => {
    setForm((f) => {
      const arr = f.active_days || [];
      return {
        ...f,
        active_days: arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort(),
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_per_hour: parseInt(form.quantity_per_hour, 10) || 0,
      };
      if (editing) {
        await api.put(`/merchants/me/offers/${editing.offer_id}`, payload);
        toast.success("Oferta actualizada");
      } else {
        await api.post("/merchants/me/offers", payload);
        toast.success("Oferta creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (o, v) => {
    try {
      await api.put(`/merchants/me/offers/${o.offer_id}`, { active: v });
      load();
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };

  const remove = async (o) => {
    if (!window.confirm(`¿Eliminar "${o.title}"?`)) return;
    try {
      await api.delete(`/merchants/me/offers/${o.offer_id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };

  const perOfferMap = (summary?.per_offer || []).reduce((acc, x) => {
    acc[x.offer_id] = x;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Ofertas</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Mensajes de match: promos, regalos y descuentos</h1>
          <p className="text-sm text-neutral-500 mt-1">Estos son los mensajes que verá el transeúnte cuando entre en tu radio de 50 metros.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold" data-testid="new-offer-btn">
          <Plus className="w-4 h-4" /> Nueva oferta
        </Button>
      </div>

      {offers.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="empty-offers">
          <Gift className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
          <div className="font-heading font-extrabold text-lg">Aún no tienes ofertas</div>
          <p className="text-sm text-neutral-500 mt-1">Crea tu primera promo para empezar a hacer match con transeúntes.</p>
          <Button onClick={openNew} className="mt-6 rounded-full bg-black text-white font-bold">Crear primera oferta</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {offers.map((o) => {
            const po = perOfferMap[o.offer_id];
            return (
              <div key={o.offer_id} className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid={`offer-card-${o.offer_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase text-[#FF007A]">
                    {o.kind === "gift" ? <Sparkles className="w-3.5 h-3.5" /> : <Tag className="w-3.5 h-3.5" />}
                    {o.kind}
                  </div>
                  <Switch checked={!!o.active} onCheckedChange={(v) => toggleActive(o, v)} />
                </div>
                <div className="font-heading font-extrabold text-xl mt-2">{o.title}</div>
                <p className="text-sm text-neutral-600 mt-1 line-clamp-3">{o.description}</p>
                {o.discount_text && (
                  <div className="mt-2 inline-block text-xs font-bold text-white bg-[#FF007A] px-2 py-1 rounded-full">{o.discount_text}</div>
                )}

                {/* Meta row */}
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
                  <span className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 rounded-full px-2 py-0.5">
                    <Clock className="w-3 h-3" /> {o.active_start || "00:00"} - {o.active_end || "23:59"}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 rounded-full px-2 py-0.5">
                    <Package className="w-3 h-3" /> {o.quantity_per_hour > 0 ? `${o.quantity_per_hour}/h` : "Sin límite"}
                  </span>
                  {po && po.quantity_per_hour > 0 && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">
                      <TrendingUp className="w-3 h-3" /> Quedan {po.remaining_this_hour} esta hora
                    </span>
                  )}
                  {po && (
                    <span className="inline-flex items-center gap-1 bg-[#0055FF]/10 text-[#0055FF] rounded-full px-2 py-0.5">
                      {po.total_delivered} entregadas
                    </span>
                  )}
                </div>

                {o.gift_products && (
                  <div className="mt-3 text-xs text-neutral-600">
                    <span className="font-bold text-neutral-800">Regalo(s): </span>{o.gift_products}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(o)} className="rounded-full">
                    <Edit3 className="w-3.5 h-3.5" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(o)} className="rounded-full text-red-500 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold">
              {editing ? "Editar oferta" : "Nueva oferta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white"
                  data-testid="offer-kind-select"
                >
                  <option value="offer">Oferta</option>
                  <option value="promo">Promoción</option>
                  <option value="gift">Regalo</option>
                </select>
              </div>
              <div>
                <Label>Texto de descuento</Label>
                <Input
                  value={form.discount_text}
                  onChange={(e) => setForm({ ...form, discount_text: e.target.value })}
                  placeholder="-30% / 2x1 / GRATIS"
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label>Título (aparece en la notificación)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Café 2x1 hoy" className="mt-1.5 h-11 rounded-xl" data-testid="offer-title-input" />
            </div>

            <div>
              <Label>Descripción del mensaje de match</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ej: '¡Estás cerca! Trae este match y llévate 2 cafés por el precio de uno hasta las 11 am. Solo hoy.'"
                className="mt-1.5 rounded-xl"
                rows={3}
                data-testid="offer-desc-input"
              />
            </div>

            {form.kind === "gift" && (
              <div>
                <Label>Productos de regalo</Label>
                <Textarea
                  value={form.gift_products}
                  onChange={(e) => setForm({ ...form, gift_products: e.target.value })}
                  placeholder="Ej: Muffin de chocolate, sticker HOLA RADAR, sorbo de café"
                  className="mt-1.5 rounded-xl"
                  rows={2}
                  data-testid="offer-gifts-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Enumera los regalos que entregarás al cliente cuando muestre el match.</p>
              </div>
            )}

            <div>
              <Label>Cantidad por hora (0 = sin límite)</Label>
              <Input
                type="number"
                min={0}
                value={form.quantity_per_hour}
                onChange={(e) => setForm({ ...form, quantity_per_hour: e.target.value })}
                className="mt-1.5 h-11 rounded-xl"
                data-testid="offer-qph-input"
              />
              <p className="text-xs text-neutral-500 mt-1">Cuántos usuarios pueden recibir esta oferta cada hora. Al agotarse la cuota, la oferta pausa hasta la siguiente hora.</p>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-[#FAFAFA] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#0055FF]" />
                <div className="font-heading font-bold text-sm">Horario de activación</div>
              </div>
              <div className="flex items-center gap-1.5 mb-3">
                {DAY_LABELS.map((lbl, i) => {
                  const selected = form.active_days?.includes(i);
                  return (
                    <button
                      key={`day-${lbl}`}
                      type="button"
                      onClick={() => toggleDay(i)}
                      title={DAY_NAMES[i]}
                      data-testid={`day-toggle-${i}`}
                      className={`w-9 h-9 rounded-full text-xs font-bold transition-colors ${
                        selected ? "bg-[#0055FF] text-white" : "bg-white border border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                      }`}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input
                    type="time"
                    value={form.active_start}
                    onChange={(e) => setForm({ ...form, active_start: e.target.value })}
                    className="mt-1 h-10 rounded-lg"
                    data-testid="offer-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input
                    type="time"
                    value={form.active_end}
                    onChange={(e) => setForm({ ...form, active_end: e.target.value })}
                    className="mt-1 h-10 rounded-lg"
                    data-testid="offer-end-input"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.description} className="rounded-full bg-black text-white font-bold" data-testid="save-offer-btn">
              {saving ? "Guardando..." : editing ? "Actualizar" : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
